import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useNodesData,
  useReactFlow,
  type EdgeProps,
} from "@xyflow/react";
import Box from "@mui/material/Box";
import { UserCheck } from "lucide-mui";
import { VERDICTS } from "../../../components/signals/ResearchVerdictChip.tsx";
import type { ConditionEdge as ConditionEdgeType, StepData, StepNode } from "./types.ts";

const OP_SYMBOLS: Record<string, string> = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "in",
};

export function labelFor(
  source: StepData | undefined,
  data: ConditionEdgeType["data"],
): { text: string; invalid: boolean } | null {
  if (source?.kind === "trigger") {
    if (source.trigger_type !== "signal") return null;
    const min = data?.min_rating;
    if (!min) return { text: "Any match", invalid: false };
    return {
      text: min >= 5 ? "Match strength 5" : `Match strength ≥ ${min}`,
      invalid: false,
    };
  }
  if (source?.kind !== "agent") return null;
  switch (source.contract) {
    case "prospect_list":
    case "contact_list": {
      const min = data?.min_results;
      if (min == null) return { text: "When the agent finishes", invalid: false };
      const noun = source.contract === "contact_list" ? "contacts" : "prospects";
      return { text: `≥ ${min} ${noun}`, invalid: min < 0 || !Number.isInteger(min) };
    }
    case "signal_brief": {
      const verdicts = data?.verdicts ?? [];
      if (!verdicts.length) return { text: "No verdict selected", invalid: true };
      const names = verdicts.map((v) => VERDICTS.find((x) => x.value === v)?.label ?? v).join(", ");
      const conf = data?.min_confidence ? ` · confidence ≥ ${data.min_confidence}` : "";
      return { text: `${names}${conf}`, invalid: false };
    }
    case "structured": {
      if (!data?.field) return { text: "When the agent finishes", invalid: false };
      const value = Array.isArray(data.value) ? data.value.join(", ") : String(data.value ?? "?");
      return {
        text: `${data.field} ${OP_SYMBOLS[data.op ?? "eq"] ?? data.op} ${value}`,
        invalid: data.value === undefined || data.value === null || data.value === "",
      };
    }
    default:
      return { text: "When the agent finishes", invalid: false };
  }
}

export default function ConditionEdge({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps<ConditionEdgeType>) {
  const sourceNode = useNodesData<StepNode>(source);
  const { setEdges, setNodes } = useReactFlow<StepNode, ConditionEdgeType>();
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const condition = labelFor(sourceNode?.data, data);
  const gated = !!data?.requires_approval;
  // A gated connection always has a pill: it is where the gate is shown and edited.
  const label = condition ?? (gated ? { text: "", invalid: false } : null);
  const stroke = label?.invalid
    ? "var(--mui-palette-error-main)"
    : selected
      ? "var(--mui-palette-primary-main)"
      : gated
        ? "var(--mui-palette-warning-main)"
        : "var(--mui-palette-text-disabled)";

  const select = () => {
    setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n)));
    setEdges((es) => es.map((e) => ({ ...e, selected: e.id === id })));
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={{
          stroke,
          strokeWidth: selected ? 2.5 : 1.5,
          strokeDasharray: gated ? "6 3" : undefined,
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <Box
            className="nodrag nopan"
            onClick={select}
            sx={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
              cursor: "pointer",
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              fontSize: "0.7rem",
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              border: "1px solid",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              borderColor: label.invalid
                ? "error.main"
                : selected
                  ? "primary.main"
                  : gated
                    ? "warning.main"
                    : "var(--mui-palette-divider)",
              bgcolor: "var(--mui-palette-background-paper)",
              color: label.invalid ? "error.main" : "text.primary",
              boxShadow: selected ? "0 0 0 2px rgba(var(--mui-palette-primary-mainChannel) / 0.25)" : "none",
            }}
          >
            {gated && (
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "warning.main" }}
              >
                <UserCheck sx={{ fontSize: 12 }} />
                {label.text ? "Needs approval ·" : "Needs approval"}
              </Box>
            )}
            {label.text}
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
